import { pxToRem } from '@fluentsms/agentnet-web-components';
import { Box, Grid, Tooltip, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { HelpOutline } from '@material-ui/icons';
import { isValid, parse } from 'date-fns';
import { FincenReportContext, FincenReportContextInterface } from 'hooks/FincenReportContext';
import { ChangeEvent, FC, useContext, useEffect, useRef, useState } from 'react';
import AgentNetButton from 'ui-kit/components/button/AgentNetButton';
import RadioGroup from 'ui-kit/components/radios/RadioGroup';
import { AddressFields } from 'ui-kit/form/interfaces/AddressModal';
import DateFieldString from 'ui-kit/inputs/DateField/DateFieldString';
import { AgentNetTextInput } from 'ui-kit/inputs/TextField';
import '../../common/styles/fincen-radio-group.scss';
import { Transferor, ValidationState } from '../../types';
import { AddressType, MAX_TRUSTEES, TRANSFEROR_OPTIONS, TransferorType } from '../constants';
import { getInitialTrusteeData } from '../models/trustee';
import IdentificationForm from './common/IdentificationForm';
import PersonalInfoForm from './common/PersonalInfoForm';
import TransferorAddressBlock from './common/TransferorAddressBlock';
import TrusteeAccordion from './TrusteeAccordion';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    formContainer: {
      width: '100%',
      '& .MuiGrid-container': {
        width: '100%',
        margin: 0,
      },
    },
    formField: {
      marginBottom: theme.spacing(1),
      flexBasis: '50%',
      maxWidth: '50%',
    },
    endSection: {
      marginBottom: theme.spacing(4),
    },
    section: {
      marginTop: theme.spacing(2),
    },
    trusteeSection: {
      marginLeft: theme.spacing(1),
      fontSize: pxToRem(18),
      fontWeight: 600,
      color: theme.palette.text.primary,
    },
    addTrusteeButton: {
      marginLeft: theme.spacing(1),
    },
    infoIcon: {
      fontSize: theme.spacing(2.25),
      margin: theme.spacing(0, 1, 0.5, 1),
      color: theme.palette.grey[600],
      verticalAlign: 'middle',
    },
  }),
);

interface TransferorFormProps {
  transferor: Transferor;
  onUpdate: (transferor: Transferor) => void;
  index?: number;
}

const TransferorForm: FC<TransferorFormProps> = ({ transferor, onUpdate, index = 0 }) => {
  const classes = useStyles();
  const isUserEditingRef = useRef(false);
  const [validation, setValidation] = useState<ValidationState>({
    isValid: true,
    errors: [],
  });
  const fincenReportCtx = useContext<FincenReportContextInterface | null>(FincenReportContext);

  const { transferorValidationErrors, clearTransferorValidationErrors, generateButtonClicked } = fincenReportCtx ?? {};

  const contextTransferor = fincenReportCtx?.fincenReport?.transferor;
  const contextTransferorData = Array.isArray(contextTransferor)
    ? index < contextTransferor.length
      ? contextTransferor[index]
      : null
    : index === 0
    ? contextTransferor
    : null;

  const effectiveValidationErrors =
    transferorValidationErrors && transferorValidationErrors.length > 0
      ? transferorValidationErrors
      : validation.errors;

  const [formData, setFormData] = useState<Transferor>(() => {
    return transferor;
  });

  const [alternateNames, setAlternateNames] = useState<string[]>(
    transferor.alternateNames && transferor.alternateNames.length > 0 ? transferor.alternateNames : [''],
  );
  useEffect(() => {
    if (!isUserEditingRef.current) {
      const updatedTransferor = { ...transferor };

      // Automatically add one trustee if transferor is Trust and has no trustees
      if (
        updatedTransferor.transferorType === TransferorType.Trust &&
        (!updatedTransferor.trustees || updatedTransferor.trustees.length === 0)
      ) {
        const newTrustee = getInitialTrusteeData();
        updatedTransferor.trustees = [newTrustee];
        onUpdate(updatedTransferor);
      }

      setFormData(updatedTransferor);
      if (transferor.alternateNames && transferor.alternateNames.length > 0) {
        setAlternateNames(transferor.alternateNames);
      } else {
        setAlternateNames(['']);
      }
    }
  }, [transferor, onUpdate]);

  const handleAddressChange = (address: AddressFields) => {
    isUserEditingRef.current = true;
    const updatedData = {
      ...formData,
      address,
    };
    setFormData(updatedData);
    onUpdate(updatedData);
  };

  const getFieldError = (fieldName: string) => {
    // Only show inline validation errors after Generate Report button is clicked
    if (!generateButtonClicked) return undefined;

    // Always show validation errors for date fields (when generateButtonClicked is true)
    if (fieldName === 'dateOfBirth' || fieldName === 'dateTrustExecuted') {
      return effectiveValidationErrors.find((error) => {
        if (error.field.includes('trustees[')) {
          return false;
        }

        return (
          error.field === `[${index}].${fieldName}` ||
          error.field === `transferor.${index}.${fieldName}` ||
          error.field === `transferors[${index}].${fieldName}`
        );
      });
    }

    const fieldToMatch = `[${index}].${fieldName}`;

    return effectiveValidationErrors.find((error) => {
      if (error.field.includes('trustees[')) {
        return false;
      }

      // Exact match for this transferor's field
      return error.field === fieldToMatch;
    });
  };

  const clearFieldError = (fieldPath: string) => {
    if (!transferorValidationErrors) {
      return;
    }

    const fieldToMatch = fieldPath.startsWith('[') ? fieldPath : `[${index}].${fieldPath}`;

    const filteredErrors = transferorValidationErrors.filter((error) => {
      return error.field !== fieldToMatch;
    });

    if (filteredErrors.length !== transferorValidationErrors.length) {
      // Use setTransferorValidationErrors directly to avoid race condition
      fincenReportCtx?.setTransferorValidationErrors?.(filteredErrors);
    }
  };

  const updateFormData = (updates: Partial<Transferor>) => {
    const newFormData = {
      ...formData,
      ...updates,
    };
    setFormData(newFormData);
    onUpdate(newFormData);

    if (transferorValidationErrors && clearTransferorValidationErrors && !updates.trustees) {
      const updatedFields = Object.keys(updates);
      const addressFields = ['address', 'foreignBusinessAddress', 'foreignPrincipalAddress', 'primaryUSAddress'];
      const nonAddressFields = updatedFields.filter((field) => !addressFields.includes(field));

      if (nonAddressFields.length > 0) {
        const filteredErrors = transferorValidationErrors.filter((error) => {
          return !nonAddressFields.some((field) => {
            const fieldPattern = `[${index}].${field}`;
            return error.field === fieldPattern || error.field.startsWith(`${fieldPattern}.`);
          });
        });

        clearTransferorValidationErrors();
        if (filteredErrors.length > 0) {
          fincenReportCtx?.setTransferorValidationErrors?.(filteredErrors);
        }
      }
    }
  };

  const handleDateOfBirthValidationError = (fieldName: string, message: string) => {
    const fieldPath = `[${index}].${fieldName}`;
    const error = { field: fieldPath, message };
    const existingErrors = transferorValidationErrors || [];
    const filteredErrors = existingErrors.filter((e) => e.field !== fieldPath);
    fincenReportCtx?.setTransferorValidationErrors?.([...filteredErrors, error]);
  };

  const handleDateOfBirthValidationClear = (fieldName: string) => {
    const fieldPath = `[${index}].${fieldName}`;
    if (transferorValidationErrors && clearTransferorValidationErrors) {
      const filteredErrors = transferorValidationErrors.filter((error) => error.field !== fieldPath);
      clearTransferorValidationErrors();
      if (filteredErrors.length > 0) {
        fincenReportCtx?.setTransferorValidationErrors?.(filteredErrors);
      }
    }
  };

  const handleDateTrustExecutedChange = (date: string | null) => {
    updateFormData({ dateTrustExecuted: date || '' });

    // Validate the date format
    if (date && date.trim() !== '') {
      // Check if the date string is complete (not partially filled like 02/__/____)
      const isPartialDate = date.includes('_');

      if (isPartialDate) {
        // If date is incomplete, add validation error
        const fieldPath = `[${index}].dateTrustExecuted`;
        const error = { field: fieldPath, message: 'Invalid date' };
        const existingErrors = transferorValidationErrors || [];
        const filteredErrors = existingErrors.filter((e) => e.field !== fieldPath);
        fincenReportCtx?.setTransferorValidationErrors?.([...filteredErrors, error]);
      } else {
        // Parse and validate the complete date
        const parsedDate = parse(date, 'MM/dd/yyyy', new Date());
        if (!isValid(parsedDate)) {
          // Add validation error for invalid date
          const fieldPath = `[${index}].dateTrustExecuted`;
          const error = { field: fieldPath, message: 'Invalid date' };
          const existingErrors = transferorValidationErrors || [];
          const filteredErrors = existingErrors.filter((e) => e.field !== fieldPath);
          fincenReportCtx?.setTransferorValidationErrors?.([...filteredErrors, error]);
        } else {
          // Check if the date components are within valid ranges
          const parts = date.split('/');
          if (parts.length === 3) {
            const month = parseInt(parts[0], 10);
            const day = parseInt(parts[1], 10);
            const year = parseInt(parts[2], 10);

            // Validate month (1-12), day (1-31), and year (reasonable range)
            if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900 || year > 2100) {
              const fieldPath = `[${index}].dateTrustExecuted`;
              const error = { field: fieldPath, message: 'Invalid date' };
              const existingErrors = transferorValidationErrors || [];
              const filteredErrors = existingErrors.filter((e) => e.field !== fieldPath);
              fincenReportCtx?.setTransferorValidationErrors?.([...filteredErrors, error]);
            } else {
              // Clear validation error if date is valid
              clearFieldError(`[${index}].dateTrustExecuted`);
            }
          } else {
            // Clear validation error if date is valid
            clearFieldError(`[${index}].dateTrustExecuted`);
          }
        }
      }
    } else {
      // Clear validation error if date is empty
      clearFieldError(`[${index}].dateTrustExecuted`);
    }
  };

  const handleDeleteAlternateName = (idx: number) => {
    isUserEditingRef.current = true;
    const updated = alternateNames.filter((_, i) => i !== idx);
    setAlternateNames(updated);
    updateFormData({
      alternateNames: updated,
    });

    if (transferorValidationErrors && clearTransferorValidationErrors) {
      const filteredErrors = transferorValidationErrors.filter((error) => !error.field.includes('alternateNames'));
      clearTransferorValidationErrors();
      if (filteredErrors.length > 0) {
        fincenReportCtx?.setTransferorValidationErrors?.(filteredErrors);
      }
    }
  };

  return (
    <div className={classes.formContainer}>
      <Grid container direction="column" spacing={2}>
        <Grid item xs={12} className={classes.formField}>
          <RadioGroup
            label="Transferor Type"
            name="transferorType"
            selected={{ value: formData.transferorType }}
            onChange={(event) => {
              isUserEditingRef.current = true;
              const value = event.target.value;

              // If changing to Trust type and no trustees exist, create one automatically
              if (value === TransferorType.Trust && (!formData.trustees || formData.trustees.length === 0)) {
                const newTrustee = getInitialTrusteeData();
                updateFormData({
                  transferorType: value as Transferor['transferorType'],
                  trustees: [newTrustee],
                });
              } else {
                updateFormData({
                  transferorType: value as Transferor['transferorType'],
                });
              }
            }}
            options={TRANSFEROR_OPTIONS}
            row={true}
            compact={true}
            border={true}
            className="fincen-radio-group"
            required={true}
            showValidation={!!getFieldError('transferorType')}
            errs={effectiveValidationErrors}
            data-testid={`fincen-transferor-${index}-type-radio`}
          />
        </Grid>

        {formData.transferorType === TransferorType.Individual ? (
          <Grid item xs={12}>
            <PersonalInfoForm
              data={{
                firstName: formData.firstName || '',
                middleName: formData.middleName,
                lastName: formData.lastName || '',
                suffix: formData.suffix,
                dateOfBirth: formData.dateOfBirth || '',
              }}
              prefix="transferor"
              index={index}
              getFieldError={getFieldError}
              onUpdate={(updates) => {
                isUserEditingRef.current = true;
                updateFormData(updates);
              }}
              onValidationError={handleDateOfBirthValidationError}
              onValidationClear={handleDateOfBirthValidationClear}
              clearFieldError={(fieldName) => clearFieldError(`[${index}].${fieldName}`)}
              showValidation={true}
            />
          </Grid>
        ) : (
          <>
            <Grid item xs={12} sm={6}>
              <AgentNetTextInput
                label="Transferor Legal Name"
                value={formData.entityLegalName || ''}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  isUserEditingRef.current = true;
                  updateFormData({
                    entityLegalName: e.target.value,
                  });
                }}
                required
                error={!!getFieldError('entityLegalName')}
                helperText={getFieldError('entityLegalName')?.message}
                data-testid={`fincen-transferor-${index}-entity-legal-name-input`}
                fullWidth
              />
            </Grid>
            {formData.transferorType === TransferorType.Entity &&
              alternateNames.map((name, idx) => (
                <Grid item xs={12} sm={6} key={idx}>
                  <AgentNetTextInput
                    label="Alternate Name (i.e., trade name, DBA)"
                    value={name}
                    error={!!getFieldError('alternateNames')}
                    helperText={getFieldError('alternateNames')?.message}
                    onChange={(e) => {
                      isUserEditingRef.current = true;
                      const updated = [...alternateNames];
                      updated[idx] = e.target.value;
                      setAlternateNames(updated);

                      updateFormData({
                        alternateNames: updated,
                      });
                    }}
                    data-testid={`fincen-transferor-${index}-alternate-name-${idx}-input`}
                    fullWidth
                  />
                </Grid>
              ))}
          </>
        )}
      </Grid>

      <TransferorAddressBlock
        transferorType={formData.transferorType}
        address={formData.address}
        foreignBusinessNoUSLocation={formData.foreignBusinessNoUSLocation || false}
        foreignBusinessAddress={formData.foreignBusinessAddress}
        foreignPrincipalAddress={formData.foreignPrincipalAddress}
        primaryUSAddress={formData.primaryUSAddress}
        addressType={formData.addressType as AddressType}
        validation={{ isValid: true, errors: effectiveValidationErrors }}
        foreignEntity={formData.foreignEntity}
        onAddressChange={handleAddressChange}
        transferorIndex={index}
        clearFieldError={clearFieldError}
        onUpdate={(updates) => {
          isUserEditingRef.current = true;
          updateFormData(updates);
        }}
      />
      <Grid className={classes.section}>
        <IdentificationForm
          identification={formData.identification}
          onUpdate={(identification) => {
            isUserEditingRef.current = true;
            updateFormData({ identification });
          }}
          getFieldError={getFieldError}
          clearFieldError={(fieldName) => clearFieldError(`[${index}].${fieldName}`)}
          validationErrors={effectiveValidationErrors}
          showValidation={validation.errors.length > 0 || !!generateButtonClicked}
          className={classes.endSection}
          transferorType={formData.transferorType}
          fieldPrefix={`[${index}].identification`}
          transferorIndex={index}
        />
      </Grid>
      {formData.transferorType === TransferorType.Trust && (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <DateFieldString
              label="Date Trust Instrument was Executed"
              format="MM/dd/yyyy"
              placeholder="mm/dd/yyyy"
              value={formData.dateTrustExecuted || ''}
              onChange={handleDateTrustExecutedChange}
              variant="outlined"
              fullWidth
              qaAttribute={`fincen-transferor-${index}-date-trust-executed-input`}
              error={!!getFieldError('dateTrustExecuted')}
              helperText={getFieldError('dateTrustExecuted')?.message}
              showValidation={true}
              shouldPrioritizeOnChange={false}
              required
            />
          </Grid>
        </Grid>
      )}

      {formData.transferorType === TransferorType.Trust && (
        <Box display="flex" justifyContent="space-between" alignItems="center" mt={4} mb={3}>
          <Typography variant="h6" className={classes.trusteeSection}>
            Trustees
            <Tooltip title="Additional information about trustees associated with this transferor">
              <HelpOutline fontSize="small" className={classes.infoIcon} />
            </Tooltip>
          </Typography>
          <AgentNetButton
            variant="contained"
            color="primary"
            size="small"
            onClick={() => {
              isUserEditingRef.current = true;
              const newTrustee = getInitialTrusteeData();

              updateFormData({
                trustees: [...(formData.trustees || []), newTrustee],
              });
            }}
            className={classes.addTrusteeButton}
            data-testid={`fincen-transferor-${index}-add-trustee-button`}
            plusIcon
            disabled={(formData.trustees?.length || 0) >= MAX_TRUSTEES}
          >
            Add Trustee
            {(formData.trustees?.length || 0) >= MAX_TRUSTEES && ` (Max ${MAX_TRUSTEES})`}
          </AgentNetButton>
        </Box>
      )}

      {formData.transferorType === TransferorType.Trust && (
        <div>
          {formData.trustees?.map((trustee, trusteeIdx) => {
            const trusteeId = trustee.id || `trustee-${index}-${trusteeIdx}`;
            return (
              <Box mb={2} key={trusteeId}>
                <TrusteeAccordion
                  trustee={trustee}
                  validation={{ isValid: true, errors: effectiveValidationErrors }}
                  index={trusteeIdx}
                  transferorIndex={index}
                  clearFieldError={clearFieldError}
                  onUpdate={(updatedTrustee) => {
                    isUserEditingRef.current = true;
                    const updatedTrustees = [...(formData.trustees || [])];
                    updatedTrustees[trusteeIdx] = updatedTrustee;

                    updateFormData({
                      trustees: updatedTrustees,
                    });
                  }}
                  onDelete={() => {
                    // Prevent deletion of the first trustee (index 0)
                    if (trusteeIdx === 0) {
                      return;
                    }

                    isUserEditingRef.current = true;
                    const updatedTrustees = formData.trustees?.filter((_, i) => i !== trusteeIdx);

                    updateFormData({
                      trustees: updatedTrustees,
                    });
                  }}
                />
              </Box>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TransferorForm;
